import React, { useEffect, useState } from "react";
import { usePageMetadata } from "../context/PageMetadataContext";
import { Table, Card, Row, Col, Badge } from "react-bootstrap";
import { FiServer, FiCheck, FiGlobe, FiLink } from "react-icons/fi";
import { Spinner } from "react-bootstrap";
import CopyIcon from "../components/CopyIcon";
import { PiscanAPI } from "../api/piscan";
import { useLanguage } from "../context/LanguageContext";


// Core Team addresses that we know from previous data
const CORE_TEAM_IPS = ["34.47.29.105", "35.228.152.141", "34.88.93.19"];

// Define types for the data we'll receive
interface NodeDetail {
  node_id: any;
  address: string;
  city?: string;
  connected: boolean;
  country_code?: string;
  country_name?: string;
  created_at: string;
  is_validator: boolean;
  known_peer: boolean;
  last_attempt_ts?: number;
  last_error_msg?: string;
  last_error_ts?: number;
  latitude?: number;
  longitude?: number;
  region?: string;
  status: string;
  updated_at: string;
  version?: string;
  // Legacy geo object for backward compatibility
  geo?: {
    city?: string;
    country?: string;
    country_code?: string;
    isp?: string;
    lat?: number;
    lon?: number;
    org?: string;
    region?: string;
  };
}

interface QuorumSet {
  inner_sets: any[];
  threshold: number;
  validators: string[];
}

interface NodeMapData {
  active_connections: number;
  active_validator_nodes_count: number;
  address_to_node_id: Record<string, string>;
  hash_to_nodes_using_qset: Record<string, string[]>;
  hash_to_validators: Record<string, string[]>;
  known_peers_count: number;
  node_details: Record<string, NodeDetail>;
  node_id_to_address: Record<string, string>;
  node_to_qset_hash_by_id: Record<string, string>;
  node_to_quorum_set_by_id: Record<string, QuorumSet>;
  nodes_with_ids: string[];
  nodes_with_qset_data_count: number;
  nodes_with_qset_hash_count: number;
  nodes_without_ids: string[];
  phantom_validators: string[];
  phantom_validators_count: number;
  qset_hashes_pending_fetch: string[];
  quorum_sets_data_count: number;
  quorum_sets_to_find_count: number;
  timestamp: string;
  total_validators_count: number;
  validator_nodes: string[];
  validators_in_qsets: string[];
}

const Nodes: React.FC = () => {
  const [nodeMapData, setNodeMapData] = useState<NodeMapData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { setHeading, setTitle, setDescription } = usePageMetadata();
  const { t, language } = useLanguage();

  useEffect(() => {
    setHeading(t('nodes.heading'));
    setTitle(t('nodes.title'));
    setDescription(t('nodes.description'));
  }, [setHeading, setTitle, setDescription, t, language]);

  useEffect(() => {
    fetchNodeMap();
  }, []);

  const fetchNodeMap = async () => {
    try {
      setLoading(true);
      const response = await fetch(PiscanAPI.NodeMap);

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      setNodeMapData(data);
      setLoading(false);
    } catch (err) {
      setError(`Failed to fetch node map data: ${err instanceof Error ? err.message : String(err)}`);
      setLoading(false);
    }
  };

  const isCoreTeamNode = (address: string): boolean => {
    return address ? CORE_TEAM_IPS.some(ip => address.includes(ip)) : false;
  };

  const getValidatorCount = (): number => {
    if (!nodeMapData) return 0;
    return nodeMapData.active_validator_nodes_count || nodeMapData.validator_nodes?.length || 0;
  };

  // First, let's add a function to calculate how many nodes trust each node
  // You can add this function before the return statement
  const calculateTrustedByCounts = (): Record<string, number> => {
    if (!nodeMapData) return {};

    const trustedByCounts: Record<string, number> = {};

    // Initialize counts for all node IDs
    Object.keys(nodeMapData.node_id_to_address || {}).forEach(nodeId => {
      trustedByCounts[nodeId] = 0;
    });

    // Count how many nodes have each node in their quorum set
    Object.entries(nodeMapData.node_to_quorum_set_by_id || {}).forEach(([nodeId, quorumSet]) => {
      if (quorumSet && quorumSet.validators) {
        quorumSet.validators.forEach((validator: string) => {
          if (trustedByCounts[validator] !== undefined) {
            trustedByCounts[validator]++;
          } else {
            trustedByCounts[validator] = 1;
          }
        });
      }
    });

    return trustedByCounts;
  };

  // Add this function to calculate the number of nodes per country

  const calculateNodesPerCountry = (): Record<string, number> => {
    if (!nodeMapData) return {};

    const nodesPerCountry: Record<string, number> = {};

    Object.values(nodeMapData.node_details || {}).forEach(details => {
      if (details.country_name) {
        const country = details.country_name;
        if (nodesPerCountry[country]) {
          nodesPerCountry[country]++;
        } else {
          nodesPerCountry[country] = 1;
        }
      }
    });

    return nodesPerCountry;
  };

  // Calculate country statistics for connected nodes only
  const calculateConnectedNodesPerCountry = (): Record<string, number> => {
    if (!nodeMapData) return {};

    const connectedNodesPerCountry: Record<string, number> = {};

    Object.values(nodeMapData.node_details || {}).forEach(details => {
      if (details.connected && details.country_name && !details.address.includes('127.0.0.1')) {
        const country = details.country_name;
        if (connectedNodesPerCountry[country]) {
          connectedNodesPerCountry[country]++;
        } else {
          connectedNodesPerCountry[country] = 1;
        }
      }
    });

    return connectedNodesPerCountry;
  };

  // Calculate city statistics for connected nodes only
  const calculateConnectedNodesPerCity = (): Record<string, number> => {
    if (!nodeMapData) return {};

    const connectedNodesPerCity: Record<string, number> = {};

    Object.values(nodeMapData.node_details || {}).forEach(details => {
      if (details.connected && details.city && !details.address.includes('127.0.0.1')) {
        const city = details.city;
        if (connectedNodesPerCity[city]) {
          connectedNodesPerCity[city]++;
        } else {
          connectedNodesPerCity[city] = 1;
        }
      }
    });

    return connectedNodesPerCity;
  };

  const connectedCountryStats = calculateConnectedNodesPerCountry();

  if (loading) {
    return (
      <div className="d-flex justify-content-center my-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return <div className="alert alert-danger">{error}</div>;
  }

  return (
    <div className="nodes-page">


      {/* Network Statistics Overview */}
      <Row className="mb-4">
        <Col md={3} sm={6} className="mb-4 mb-md-0">
          <Card className="h-100 shadow-sm">
            <Card.Body className="d-flex align-items-center">
              <div className="rounded-circle p-3 bg-primary bg-opacity-10 me-3">
                {FiServer({ size: 24 })}
              </div>
              <div>
                <h6 className="text-muted mb-1">{t('nodes.active_nodes')}</h6>
                <h3 className="mb-0 stats-number">{nodeMapData?.active_connections}</h3>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={3} sm={6} className="mb-4 mb-md-0">
          <Card className="h-100 shadow-sm">
            <Card.Body className="d-flex align-items-center">
              <div className="rounded-circle p-3 bg-success bg-opacity-10 me-3">
                {FiCheck({ size: 24, className: "text-success" })}
              </div>
              <div>
                <h6 className="text-muted mb-1">{t('nodes.validators')}</h6>
                <h3 className="mb-0 stats-number">{getValidatorCount()}</h3>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={3} sm={6} className="mb-4 mb-md-0">
          <Card className="h-100 shadow-sm">
            <Card.Body className="d-flex align-items-center">
              <div className="rounded-circle p-3 bg-info bg-opacity-10 me-3">
                {FiGlobe({ size: 24 })}
              </div>
              <div>
                <h6 className="text-muted mb-1">{t('nodes.known_peers')}</h6>
                <h3 className="mb-0 stats-number">{nodeMapData?.known_peers_count || 0}</h3>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={3} sm={6}>
          <Card className="h-100 shadow-sm">
            <Card.Body className="d-flex align-items-center">

              <div className="rounded-circle p-3 bg-danger bg-opacity-10 me-3">
                {FiLink({ size: 24 })}
              </div>
              <div>
                <h6 className="text-muted mb-1">{t('nodes.phantom_validators')}</h6>
                <h3 className="mb-0 stats-number">{nodeMapData?.phantom_validators_count}</h3>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Monity App Promotion */}
      <Row className="mb-4">
        <Col md={12}>
          <Card className="shadow-sm card-custom-bg">
            <Card.Body className="p-4">
              <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center">
                <div>
                  <h5 className="fw-bold mb-2 card-title">Monitor your Pi Nodes 24/7, every minute.</h5>
                  <p className="mb-1 text-color-normal">Get instant alerts via Push Notifications, Email, and Telegram with the Monity app.</p>
                  <p className="mb-0 text-muted small">Monity is built by the same developer as PiScan.</p>
                </div>
                <div className="mt-3 mt-md-0 ms-md-4 text-nowrap">
                  <a href="https://apps.apple.com/us/app/monity-dev/id6757676031" target="_blank" rel="noopener noreferrer" className="btn btn-primary fw-bold px-4 py-2 rounded-pill shadow-sm">
                    View on App Store <i className="bi bi-box-arrow-up-right small ms-1"></i>
                  </a>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Country and City Statistics Tables */}
      <Row className="mb-4">
        <Col lg={6} className="mb-3">
          <Card>
            <Card.Header>
              <h4 className="card-title mb-0">{t('nodes.country_statistics')} (Top 10 - {t('nodes.online_nodes_only')})</h4>
            </Card.Header>
            <Card.Body className="p-0">
              <div className="table-responsive">
                <Table hover className="mb-0">
                  <thead>
                    <tr>
                      <th>{t('nodes.country')}</th>
                      <th className="text-end">{t('nodes.connected_nodes')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(calculateConnectedNodesPerCountry())
                      .sort(([, a], [, b]) => b - a) // Sort by count descending
                      .slice(0, 10) // Limit to top 10 countries
                      .map(([country, count]) => (
                        <tr key={country}>
                          <td>
                            <span className="fw-medium">{country}</span>
                          </td>
                          <td className="text-end">
                            <Badge bg="info" className="px-2 py-1">
                              {count}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    {Object.keys(calculateConnectedNodesPerCountry()).length === 0 && (
                      <tr>
                        <td colSpan={2} className="text-center text-muted py-4">
                          {t('nodes.no_country_data')}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={6} className="mb-3">
          <Card>
            <Card.Header>
              <h4 className="card-title mb-0">{t('nodes.city_statistics')} (Top 10 - {t('nodes.online_nodes_only')})</h4>
            </Card.Header>
            <Card.Body className="p-0">
              <div className="table-responsive">
                <Table hover className="mb-0">
                  <thead>
                    <tr>
                      <th>{t('nodes.city')}</th>
                      <th className="text-end">{t('nodes.connected_nodes')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(calculateConnectedNodesPerCity())
                      .sort(([, a], [, b]) => b - a) // Sort by count descending
                      .slice(0, 10) // Limit to top 10 cities
                      .map(([city, count]) => (
                        <tr key={city}>
                          <td>
                            <span className="fw-medium">{city}</span>
                          </td>
                          <td className="text-end">
                            <Badge bg="success" className="px-2 py-1">
                              {count}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    {Object.keys(calculateConnectedNodesPerCity()).length === 0 && (
                      <tr>
                        <td colSpan={2} className="text-center text-muted py-4">
                          {t('nodes.no_city_data')}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h2 className="card-title">{t('nodes.node_details')}</h2>
          <Badge bg="primary">
            {Object.entries(nodeMapData?.node_details || {})
              .filter(([_, details]) => !details.address.includes('127.0.0.1')).length + (nodeMapData?.phantom_validators?.length || 0)} {t('nodes.nodes')}
          </Badge>
        </Card.Header>
        <Card.Body className="p-0">
          <div className="table-responsive">
            <Table hover className="mb-0">
              <thead>
                <tr>
                  <th>{t('nodes.status')}</th>
                  <th>{t('nodes.node_id')}</th>
                  <th>{t('nodes.address')}</th>
                  <th>{t('nodes.type')}</th>
                  <th>{t('nodes.location')}</th>
                </tr>
              </thead>
              <tbody>
                {/* Combine and sort all nodes including phantom validators */}
                {[
                  // Regular nodes from node_details
                  ...Object.entries(nodeMapData?.node_details || {})
                    .filter(([_, details]) => !details.address.includes('127.0.0.1'))
                    .map(([nodekey, details]) => ({
                      type: 'regular',
                      key: nodekey,
                      details,
                      nodeId: details.node_id,
                      isValidator: details.is_validator,
                      connected: details.connected,
                      hasNodeId: !!details.node_id
                    })),

                  // Phantom validators
                  ...(nodeMapData?.phantom_validators || []).map((phantomNodeId: string) => ({
                    type: 'phantom',
                    key: `phantom-${phantomNodeId}`,
                    nodeId: phantomNodeId,
                    isValidator: true,
                    connected: false,
                    hasNodeId: true
                  }))
                ]
                  .sort((a, b) => {
                    // Sort by node_id existence first (nodes with node_id go to top)
                    if (a.hasNodeId && !b.hasNodeId) return -1;
                    if (!a.hasNodeId && b.hasNodeId) return 1;

                    // Then sort by validator status
                    if (a.isValidator && !b.isValidator) return -1;
                    if (!a.isValidator && b.isValidator) return 1;

                    // Within validators, regular validators come before phantom
                    if (a.isValidator && b.isValidator) {
                      if (a.type === 'regular' && b.type === 'phantom') return -1;
                      if (a.type === 'phantom' && b.type === 'regular') return 1;
                    }

                    // Then by connectivity
                    if (a.connected && !b.connected) return -1;
                    if (!a.connected && b.connected) return 1;

                    return a.key.localeCompare(b.key);
                  })
                  .map((item) => {
                    if (item.type === 'regular') {
                      const { nodekey, details } = item as any;
                      const nodeId = details.node_id;
                      const quorumSet = nodeId ? nodeMapData?.node_to_quorum_set_by_id[nodeId] : null;
                      const connectionCount = quorumSet?.validators.length || 0;
                      const isCoreTeam = isCoreTeamNode(details.address || nodekey);

                      // Get node color indicator
                      const isConnected = details.connected;
                      const isValidator = details.is_validator;
                      let statusColor = '#cccccc';
                      if (isConnected) {
                        if (isValidator) {
                          statusColor = isCoreTeam ? '#ff9900' : '#1a85ff';
                        } else {
                          statusColor = isCoreTeam ? '#ffd699' : '#a0d0ff';
                        }
                      }

                      return (
                        <tr key={nodekey}>
                          <td>
                            <div className="d-flex align-items-center">
                              <span
                                className="d-inline-block me-2"
                                style={{
                                  width: '12px',
                                  height: '12px',
                                  backgroundColor: statusColor,
                                  borderRadius: '50%'
                                }}
                              ></span>
                              {details.connected ? (
                                <Badge bg="success" className="small">{t('nodes.status.connected')}</Badge>
                              ) : (
                                <Badge bg="danger" className="small">{t('nodes.status.disconnected')}</Badge>
                              )}
                            </div>
                          </td>
                          <td>
                            {nodeId ? (
                              <div className="d-flex align-items-center">
                                <code className="small text-truncate" style={{ maxWidth: '120px' }}>
                                  {nodeId.substr(0, 8)}...{nodeId.substr(-8)}
                                </code>
                                <CopyIcon textToCopy={nodeId} />
                              </div>
                            ) : (
                              <span className="text-muted small">Unknown</span>
                            )}
                          </td>
                          <td>
                            <code className="small">
                              {details.address}
                            </code>
                          </td>
                          <td>
                            <div>
                              {details.is_validator ? (
                                <Badge bg="primary" className="small">{t('nodes.validator')}</Badge>
                              ) : (
                                <Badge bg="secondary" className="small">{t('nodes.watcher')}</Badge>
                              )}
                              {isCoreTeam && <Badge bg="warning" className="ms-1 small">{t('nodes.core_team')}</Badge>}
                            </div>
                          </td>
                          <td>
                            <div className="small">
                              {details.city && details.country_name ? (
                                `${details.city}, ${details.country_name}`
                              ) : details.country_name ? (
                                details.country_name
                              ) : details.city ? (
                                details.city
                              ) : (
                                <span className="text-muted">Unknown</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    } else {
                      // Phantom validator
                      const phantomNodeId = item.nodeId;
                      return (
                        <tr key={item.key}>
                          <td>
                            <div className="d-flex align-items-center">
                              <span
                                className="d-inline-block me-2"
                                style={{
                                  width: '12px',
                                  height: '12px',
                                  backgroundColor: '#dc3545', // Red for unreachable validators
                                  borderRadius: '50%'
                                }}
                              ></span>
                              <Badge bg="danger" className="small">Unreachable</Badge>
                            </div>
                          </td>
                          <td>
                            <div className="d-flex align-items-center">
                              <code className="small text-truncate" style={{ maxWidth: '120px' }}>
                                {phantomNodeId.substr(0, 8)}...{phantomNodeId.substr(-8)}
                              </code>
                              <CopyIcon textToCopy={phantomNodeId} />
                            </div>
                          </td>
                          <td>
                            <code className="small text-muted">Unknown</code>
                          </td>
                          <td>
                            <div>
                              <Badge bg="primary" className="small">{t('nodes.validator')}</Badge>
                              <Badge bg="danger" className="ms-1 small">Unreachable</Badge>
                            </div>
                          </td>
                          <td>
                            <div className="small">
                              <span className="text-muted">Unknown</span>
                            </div>
                          </td>
                        </tr>
                      );
                    }
                  })}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>

    </div>
  );
};

export default Nodes;